import React, { useState, useEffect, useRef, useCallback } from "react";
import toast, { Toaster } from "react-hot-toast";
import tmi from "tmi.js";
import config from "../config";
import Navbar from "../components/Navbar";
import GuessGrid from "../components/GuessGrid";
import Keyboard from "../components/Keyboard";

const WORD_LENGTH = 6;
const MAX_GUESSES = 7;


function TwitchLettro() {
  const didInit = useRef(false);
  const [answer, setAnswer] = useState("");
  const [guesses, setGuesses] = useState([]);
  const [currentGuess, setCurrentGuess] = useState("");
  const [status, setStatus] = useState("playing");
  const [validWords, setValidWords] = useState(new Set());
  const [channel, setChannel] = useState("");
  const [chatCount, setChatCount] = useState(0);
  const [guessCount, setGuessCount] = useState(0);
  const [wordMap, setWordMap] = useState({});

  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;

    async function fetchData() {
      try {
        const wordListId = "wordList-v2";
        const cachedWords = localStorage.getItem(wordListId);
        let wordList;
        if (cachedWords) {
          wordList = JSON.parse(cachedWords);
        } else {
          const wordRes = await fetch(`${config.apiBaseUrl}/words?type=twitch`);
          const wordListData = await wordRes.json();
          wordList = wordListData.map((w) => w.word);
          localStorage.setItem(wordListId, JSON.stringify(wordList));
        }
        const answerRes = await fetch(`${config.apiBaseUrl}/word-of-the-day?type=twitch`);
        const answerData = await answerRes.json();
        setAnswer(answerData.word.toUpperCase());
        setValidWords(new Set(wordList));
      } catch (err) {
        console.error("Error loading data:", err);
        toast.error("Could not load game data.");
      }
    }
    fetchData();
  }, []);

  const submitGuess = useCallback((overrideGuess) => {
    const guess = (overrideGuess || currentGuess).toUpperCase();

    if (guess.length !== WORD_LENGTH) {
      toast.error(`Guess must be ${WORD_LENGTH} letters.`);
      return;
    }
    if (!validWords.has(guess.toUpperCase())) {
      toast.error("Word not in list.");
      return;
    }
    if (guesses.includes(guess)) {
      toast.error("Already guessed.");
      return;
    }

    const newGuesses = [...guesses, guess];
    setGuesses(newGuesses);
    setCurrentGuess("");

    // remove from word cloud
    setWordMap((prev) => {
      const newMap = { ...prev };
      delete newMap[guess];
      return newMap;
    });

    if (guess === answer) {
      setStatus("won");
      toast("Congrats! See you tomorrow!", { icon: "🎉" });
      let audio = new Audio('sounds/success.mp3');
      audio.play();
    } else if (newGuesses.length >= MAX_GUESSES) {
      setStatus("lost");
      toast("Game over! The word was: " + answer, { icon: "😞" });
    }
  }, [currentGuess, guesses, validWords, answer]);

  const onKeyDown = useCallback(
    (e) => {
      const activeTag = document.activeElement.tagName;
      if (activeTag === "INPUT" || activeTag === "TEXTAREA") return;

      if (status !== "playing") return;
      if (e.key === "Enter") submitGuess();
      else if (e.key === "Backspace") setCurrentGuess((g) => g.slice(0, -1));
      else if (/^[a-zA-Z]$/.test(e.key)) {
        if (currentGuess.length < WORD_LENGTH) {
          setCurrentGuess((g) => g + e.key.toUpperCase());
        }
      }
    },
    [currentGuess, status, submitGuess]
  );

  useEffect(() => {
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onKeyDown]);

  const getFeedback = (guess) => {
    const result = Array(WORD_LENGTH).fill("absent");
    const answerChars = answer.split("");
    const guessChars = guess.split("");
    for (let i = 0; i < WORD_LENGTH; i++) {
      if (guessChars[i] === answerChars[i]) {
        result[i] = "correct";
        answerChars[i] = null;
      }
    }
    for (let i = 0; i < WORD_LENGTH; i++) {
      if (result[i] === "correct") continue;
      const idx = answerChars.indexOf(guessChars[i]);
      if (idx !== -1) {
        result[i] = "present";
        answerChars[idx] = null;
      }
    }
    return result;
  };

  const handleTwitchConnect = () => {
    if (!channel) return;
    const client = new tmi.Client({ channels: [channel] });
    toast("Connected to " + channel, { icon: "😁" });
    client.connect();
    client.on("message", (channel, tags, message) => {
      setChatCount((count) => count + 1);
      const word = message.trim().toUpperCase();
      if (word.length === WORD_LENGTH && !guesses.includes(word) && !["NIGGER", "FAGGOT"].includes(word)) {
        setGuessCount((count) => count + 1);
        setWordMap((prev) => {
          const updated = { ...prev };
          updated[word] = updated[word] ? { ...updated[word], count: updated[word].count + 1 } : { count: 1, users: new Set() };
          updated[word].users.add(tags.username);
          return updated;
        });
      }
    });
  };

  const keyboardState = guesses.reduce((acc, guess) => {
    const feedback = getFeedback(guess);
    for (let i = 0; i < WORD_LENGTH; i++) {
      const letter = guess[i];
      if (feedback[i] === "correct") acc[letter] = "correct";
      else if (feedback[i] === "present" && acc[letter] !== "correct") acc[letter] = "present";
      else if (!acc[letter]) acc[letter] = "absent";
    }
    return acc;
  }, {});

  const sortedWords = Object.entries(wordMap)
    .filter(([word]) => !guesses.includes(word))
    .sort((a, b) => b[1].count - a[1].count)
    .map(([word, data]) => `${word} x${data.count}`);

  const keyboardRows = [
    "QWERTYUIOP".split(""),
    "ASDFGHJKL".split(""),
    ["Enter", ..."ZXCVBNM".split(""), "Backspace"],
  ];

  const onScreenKeyClick = (key) => {
    if (status !== "playing") return;
    if (key === "Enter") submitGuess();
    else if (key === "Backspace") setCurrentGuess((g) => g.slice(0, -1));
    else if (/^[A-Z]$/.test(key)) {
      if (currentGuess.length < WORD_LENGTH) {
        setCurrentGuess((g) => g + key);
      }
    }
  };

  document.title = "Plaay.nl · Lettro with Twitch Daily Game";
  return (
    <>
      <Navbar gameLogo="/lettro.png" label="with Twitch - Daily" />
      <Toaster position="top-center" />
      <main className="game-container" style={{ display: "flex", gap: "2rem", maxWidth:"960px" }}>
        <div style={{ flex: 1, maxWidth:"360px" }}>
          <GuessGrid guesses={guesses} currentGuess={currentGuess} getFeedback={getFeedback} wordLength={WORD_LENGTH} maxGuesses={MAX_GUESSES} />
          {status === "playing" && <p>Type your guess and press Enter</p>}
          {status === "won" && <p>🎉 Congrats! You guessed the word: {answer}</p>}
          {status === "lost" && <p>😞 Game over! The word was: {answer}</p>}
          <Keyboard keyboardRows={keyboardRows} keyboardState={keyboardState} onKeyClick={onScreenKeyClick} />
        </div>

        <div style={{ flex: 1 }}>
          <h2 style={{fontSize:"2.3em"}}>Hey chat, play along! Type your 6-letter guess in chat.</h2>
          <div style={{ marginBottom: "1rem" }}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleTwitchConnect();
            }}
            style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
          >
              <input
                type="text"
                placeholder="Enter Twitch channel"
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
                style={{ width: "95%", padding: "0.5rem", marginBottom: "-15px" }}
              />
              <button style={{marginLeft:"-4px"}}>Join Chat</button>
            </form>
          </div>
          <p style={{marginTop:"2em"}}>{chatCount} chats, {guessCount} guesses</p>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "0.5rem",
              cursor: "pointer",
            }}
          >
            {sortedWords.map((wordStr, i) => {
              const word = wordStr.split(" ")[0];
              const count = wordMap[word].count;
              return (
                <span
                  key={i}
                  onClick={() => {
                    if (status !== "playing") return;
                    submitGuess(word);
                  }}
                  style={{
                    fontSize: `1.1rem`,
                    background: "#333",
                    padding: "0.4rem 0.6rem",
                    borderRadius: "4px",
                    userSelect: "none",
                  }}
                >
                  {word} x{count}
                </span>
              );
            })}
          </div>

        </div>
      </main>
    </>
  );
}

export default TwitchLettro;
